import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../../components/ui/tabs";

const STATUS_BADGE = {
    received: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    approved: "bg-[#00b67a]/15 text-[#00b67a] border-[#00b67a]/30",
    rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function AdminApplications() {
    const [tab, setTab] = useState("received");
    const [items, setItems] = useState([]);

    const load = async (status) => {
        try {
            const { data } = await api.get(`/applications?status=${status}`);
            setItems(data);
        } catch {}
    };
    useEffect(() => { load(tab); }, [tab]);

    const update = async (id, status) => {
        try {
            await api.patch(`/applications/${id}`, { status });
            toast.success(`Application ${status}`);
            load(tab);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        }
    };

    return (
        <div data-testid="admin-applications" className="space-y-6">
            <div>
                <h1 className="font-display font-bold text-3xl tracking-tight">
                    Applications
                </h1>
                <p className="text-muted-foreground mt-1">
                    Review incoming sign-ups. Approving activates the user account.
                </p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-card border border-white/10">
                    <TabsTrigger value="received" data-testid="apps-tab-received">
                        Pending
                    </TabsTrigger>
                    <TabsTrigger value="approved" data-testid="apps-tab-approved">
                        Approved
                    </TabsTrigger>
                    <TabsTrigger value="rejected" data-testid="apps-tab-rejected">
                        Rejected
                    </TabsTrigger>
                </TabsList>
                <TabsContent value={tab} className="mt-5">
                    {items.length === 0 ? (
                        <div className="bg-card border border-white/[0.07] rounded-2xl p-12 text-center text-muted-foreground text-sm">
                            No applications in this state.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {items.map((a) => (
                                <article
                                    key={a.id}
                                    data-testid={`application-row-${a.id}`}
                                    className="bg-card border border-white/[0.07] rounded-2xl p-5"
                                >
                                    <div className="flex items-start justify-between gap-4 mb-4">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-display font-semibold">
                                                    {a.full_name}
                                                </h3>
                                                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_BADGE[a.status]}`}>
                                                    {a.status}
                                                </span>
                                            </div>
                                            <p className="text-xs text-muted-foreground font-mono">
                                                {a.email} · {a.company_name}{" "}
                                                {a.company_website ? `· ${a.company_website}` : ""}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs font-mono text-muted-foreground">
                                                {new Date(a.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-3 text-xs mb-4">
                                        <div>
                                            <p className="text-muted-foreground">Monthly spend</p>
                                            <p className="font-mono">{a.monthly_spend}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Country</p>
                                            <p className="font-mono">{a.country || "—"}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Platform</p>
                                            <p className="font-mono">{a.platform || "All"}</p>
                                        </div>
                                    </div>
                                    {a.message && (
                                        <p className="text-sm text-foreground/85 bg-background/50 rounded-lg p-3 mb-4 border border-white/5">
                                            {a.message}
                                        </p>
                                    )}
                                    {a.status === "received" && (
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => update(a.id, "approved")}
                                                data-testid={`approve-${a.id}`}
                                                className="bg-[#00b67a] hover:bg-[#00b67a]/90 text-white"
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                onClick={() => update(a.id, "rejected")}
                                                variant="outline"
                                                data-testid={`reject-${a.id}`}
                                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                            >
                                                Reject
                                            </Button>
                                        </div>
                                    )}
                                </article>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
